'use client'

import { trackCalendlyBooked, trackCalendlyClick } from '@/utils/tracking'
import { usePathname } from 'next/navigation'
import Script from 'next/script'
import { ReactNode, useEffect, useRef } from 'react'

interface CalendlyEmbedProps {
  url?: string
  variant?: 'inline' | 'popup'
  text?: string
  color?: string
  textColor?: string
  className?: string
  prefill?: {
    name?: string
    email?: string
    customAnswers?: {
      a1?: string
      a2?: string
      a3?: string
    }
  }
  icon?: ReactNode
  /** Herkunft für Analytics-Events, z.B. 'floating-cta'; Default ist der Seitenpfad. */
  trackingSource?: string
  /**
   * Eigenes Button-Markup für variant="popup". Wird anstelle von icon/text
   * gerendert, damit Aufrufer ihr Layout behalten können, ohne auf einen
   * externen <a target="_blank"> auszuweichen (dort greift der
   * event_scheduled-Listener nicht, die Buchung wäre nicht messbar).
   */
  children?: ReactNode
}

/**
 * Der Listener für die Buchungs-Conversion liegt bewusst MODULWEIT und nicht in
 * der Komponente. Vorher registrierte jede gemountete CalendlyEmbed-Instanz
 * ihren eigenen window-message-Listener. Auf einer Landingpage sind das bis zu
 * vier (CTACard, Alternativ-Link im Formular, Erfolgs-Ansicht, FloatingCTA),
 * eine einzige Buchung hat damit vier Conversions an Google Ads gemeldet.
 * Nachgewiesen im E2E-Test am 02.09.2026.
 */
let listenerRefCount = 0
let detachListener: (() => void) | null = null
/** Herkunft der zuletzt geöffneten Buchungsstrecke, für die Analytics-Zuordnung. */
let activeSource = ''

function attachBookingListener() {
  listenerRefCount += 1
  if (detachListener) return

  const handleMessage = (e: MessageEvent) => {
    // Origin-Prüfung ist die Sicherheitsgrenze: ohne sie könnte jede Seite im
    // selben Tab eine Conversion auslösen.
    if (e.origin !== 'https://calendly.com') return
    if (e.data?.event !== 'calendly.event_scheduled') return
    trackCalendlyBooked(activeSource || window.location.pathname)
  }

  window.addEventListener('message', handleMessage)
  detachListener = () => window.removeEventListener('message', handleMessage)
}

function releaseBookingListener() {
  listenerRefCount = Math.max(0, listenerRefCount - 1)
  if (listenerRefCount === 0 && detachListener) {
    detachListener()
    detachListener = null
  }
}

const WIDGET_SRC = 'https://assets.calendly.com/assets/external/widget.js'
/** Wie lange ein Klick auf widget.js wartet, bevor der Kalender im neuen Tab aufgeht. */
const WIDGET_TIMEOUT_MS = 4000

/**
 * widget.js kommt per next/script mit lazyOnload, also erst nach dem
 * load-Event der Seite. Wer mobil frueh tippt, klickte bisher ins Leere:
 * window.Calendly fehlte, der Klick wurde trotzdem als calendly_click gezaehlt
 * und es passierte nichts. Jetzt laedt der Klick das Skript bei Bedarf selbst.
 * Das Promise wird geteilt, damit mehrere Buttons das Skript nur einmal holen;
 * scheitert es, wird es verworfen und der naechste Klick versucht es erneut.
 */
let widgetPromise: Promise<void> | null = null

function loadCalendlyWidget(): Promise<void> {
  if (window.Calendly) return Promise.resolve()
  if (widgetPromise) return widgetPromise

  widgetPromise = new Promise<void>((resolve, reject) => {
    // next/script hat das Tag eventuell schon eingehaengt, dann nur warten
    if (!document.querySelector(`script[src="${WIDGET_SRC}"]`)) {
      const script = document.createElement('script')
      script.src = WIDGET_SRC
      script.async = true
      script.onerror = () => reject(new Error('Calendly widget.js konnte nicht geladen werden'))
      document.head.appendChild(script)
    }

    // Auf window.Calendly pruefen statt auf das load-Event: das bereits
    // vorhandene Tag kann laengst geladen sein, ohne dass wir es mitbekommen.
    const started = Date.now()
    const poll = () => {
      if (window.Calendly) return resolve()
      if (Date.now() - started >= WIDGET_TIMEOUT_MS) return reject(new Error('Calendly widget.js Timeout'))
      window.setTimeout(poll, 50)
    }
    poll()
  }).catch((error) => {
    widgetPromise = null
    throw error
  })

  return widgetPromise
}

/**
 * Notweg, wenn widget.js nicht kommt (Blocker, Funkloch): Kalender in einem
 * neuen Tab. Nach bis zu 4 s Wartezeit kann Safari das als Popup ohne
 * Nutzergeste blockieren; dann wechselt der aktuelle Tab zum Kalender, damit
 * der Klick in jedem Fall zu einem buchbaren Termin fuehrt. Ohne noopener im
 * Feature-String, weil window.open dann immer null liefert und eine Sperre
 * nicht erkennbar waere; opener wird stattdessen von Hand gekappt.
 */
function openCalendlyFallback(url: string) {
  const win = window.open(url, '_blank')
  if (win) {
    win.opener = null
    return
  }
  window.location.assign(url)
}

export default function CalendlyEmbed({
  url = 'https://calendly.com/kommers/meet',
  variant = 'inline',
  text = 'Termin vereinbaren',
  color = '#ff6b35',
  className = '',
  prefill,
  icon,
  trackingSource,
  children
}: CalendlyEmbedProps) {
  const pathname = usePathname()
  const source = trackingSource || pathname

  // Calendly meldet eine abgeschlossene Buchung per postMessage aus dem iframe,
  // das ist die eigentliche Conversion, nicht der CTA-Klick. Der Listener wird
  // modulweit gehalten und hier nur referenzgezählt an- und abgemeldet.
  useEffect(() => {
    attachBookingListener()
    // Das eingebettete Widget ist die Buchungsstrecke, sobald es sichtbar ist.
    // Beim Popup setzt erst der Klick die Herkunft.
    if (variant === 'inline') activeSource = source
    return releaseBookingListener
  }, [source, variant])

  // Sperrt Mehrfachklicks, solange widget.js noch nachlaedt. Sonst zaehlt
  // jeder ungeduldige Tipp ein weiteres calendly_click.
  const opening = useRef(false)

  const handlePopupClick = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (opening.current) return
    opening.current = true
    activeSource = source

    try {
      await loadCalendlyWidget()
      window.Calendly.initPopupWidget({
        url,
        prefill,
        pageSettings: {
          backgroundColor: 'ffffff',
          hideEventTypeDetails: false,
          hideLandingPageDetails: false,
          primaryColor: color,
          textColor: '#4d5055'
        }
      })
    } catch {
      openCalendlyFallback(url)
    } finally {
      opening.current = false
    }
    // Erst zaehlen, wenn tatsaechlich ein Kalender aufgegangen ist
    trackCalendlyClick(source)
  }

  if (variant === 'popup') {
    return (
      <>
        <Script src="https://assets.calendly.com/assets/external/widget.js" strategy="lazyOnload" />
        <link href="https://assets.calendly.com/assets/external/widget.css" rel="stylesheet" />
        <button
          onClick={handlePopupClick}
          type="button"
          className={children ? className : className || `bg-accent text-white px-8 py-4 rounded-3xl font-medium hover:bg-accent/90 transition-opacity`}
        >
          {children ?? (
            <>
              {icon} {text}
            </>
          )}
        </button>
      </>
    )
  }

  if (variant === 'inline') {
    return (
      <>
        <Script src="https://assets.calendly.com/assets/external/widget.js" strategy="lazyOnload" />
        <div
          className={`calendly-inline-widget ${className}`}
          data-url={`${url}?background_color=1a1a1a&text_color=ffffff&primary_color=${color.replace('#', '')}`}
          // Kein minWidth 320px mehr: in einem Container mit Innenabstand
          // ragte das Widget auf 320 bis 360px Breite seitlich heraus und die
          // ganze Seite liess sich quer verschieben.
          style={{ width: '100%', minWidth: 0, height: '700px' }}
        />
      </>
    )
  }
  return null
}

// Add TypeScript declaration for Calendly
interface CalendlyOptions {
  url: string
  text?: string
  color?: string
  textColor?: string
  branding?: boolean
  prefill?: {
    name?: string
    email?: string
    customAnswers?: {
      a1?: string
      a2?: string
      a3?: string
    }
  }
  pageSettings?: {
    backgroundColor?: string
    hideEventTypeDetails?: boolean
    hideLandingPageDetails?: boolean
    primaryColor?: string
    textColor?: string
  }
  parentElement?: HTMLElement
}

declare global {
  interface Window {
    Calendly: {
      initBadgeWidget: (options: CalendlyOptions) => void
      initPopupWidget: (options: CalendlyOptions) => void
      initInlineWidget: (options: CalendlyOptions) => void
    }
  }
}
